import React from "react";
import MuiTextField from "@mui/material/TextField";
import PropTypes from "prop-types";

/**
 * TextField component wrapper for text input.
 * Wraps Material-UI TextField with theme integration.
 * 
 * @component
 * @example
 * <TextField
 *   label="Email"
 *   variant="outlined"
 *   value={email}
 *   onChange={handleChange}
 * />
 */
const TextField = (props) => {
  return <MuiTextField {...props} />;
};

TextField.propTypes = {
  /**
   * The label content
   */
  label: PropTypes.node,
  /**
   * The variant to use
   */
  variant: PropTypes.oneOf(["standard", "filled", "outlined"]),
  /**
   * The value of the input element
   */
  value: PropTypes.any,
  /**
   * The default value
   */
  defaultValue: PropTypes.any,
  /**
   * If true, the input element will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * If true, the label will be displayed in an error state
   */
  error: PropTypes.bool,
  /**
   * If true, the input will take up the full width of its container
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content
   */
  helperText: PropTypes.node,
  /**
   * The id of the input element
   */
  id: PropTypes.string,
  /**
   * Name attribute of the input element
   */
  name: PropTypes.string,
  /**
   * Callback fired when the value is changed
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when the input loses focus
   */
  onBlur: PropTypes.func,
  /**
   * The short hint displayed in the input before the user enters a value
   */
  placeholder: PropTypes.string,
  /**
   * If true, the input element will be required
   */
  required: PropTypes.bool,
  /**
   * The size of the component
   */
  size: PropTypes.oneOf(["small", "medium"]),
  /**
   * Type of the input element
   */
  type: PropTypes.string,
  /**
   * If true, the input element will support multiline text
   */
  multiline: PropTypes.bool,
  /**
   * Number of rows to display when multiline option is set to true
   */
  rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Maximum number of rows to display when multiline option is set to true
   */
  maxRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Minimum number of rows to display when multiline option is set to true
   */
  minRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Props applied to the Input element
   */
  InputProps: PropTypes.object,
  /**
   * Props applied to the InputLabel element
   */
  InputLabelProps: PropTypes.object,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI TextField behavior
// MUI TextField defaults: variant="outlined"

export default TextField;

